import {
  List,
  ListFormData,
  ListItem,
  ListStats,
  BulkOperation,
  DateScopedListData
} from '../types/lists';
import { universalCompletionService } from './universalCompletionService';
import { templatesService } from './templatesService';

const LISTS_STORAGE_KEY = 'godmode_lists';

export class ListsService {
  private lists: List[] = [];

  constructor() {
    this.loadLists();
    // Ensure initial data has unique priorities
    this.normalizeAllPriorities();
  }

  // Load lists from localStorage
  private loadLists(): void {
    try {
      const storedLists = localStorage.getItem(LISTS_STORAGE_KEY);
      if (storedLists) {
        const parsed = JSON.parse(storedLists);
        this.lists = parsed.map((list: any) => ({
          ...list,
          deadline: list.deadline ? new Date(list.deadline) : undefined,
          startDate: list.startDate ? new Date(list.startDate) : undefined,
          completedDate: list.completedDate ? new Date(list.completedDate) : undefined,
          createdAt: new Date(list.createdAt),
          updatedAt: new Date(list.updatedAt),
          items: list.items.map((item: any) => ({
            ...item,
            completedAt: item.completedAt ? new Date(item.completedAt) : undefined,
            createdAt: new Date(item.createdAt),
          }))
        }));
      }
    } catch (error) {
      console.error('Error loading lists:', error);
      this.lists = [];
    }
  }

  // Save lists to localStorage
  private saveLists(): void {
    try {
      localStorage.setItem(LISTS_STORAGE_KEY, JSON.stringify(this.lists));
    } catch (error) {
      console.error('Error saving lists:', error);
    }
  }

  // Initialize with mock data if empty
  initializeMockData(): void {
    // Reload lists from localStorage in case persona data was loaded after initial service construction
    this.loadLists();
    this.normalizeAllPriorities();

    if (this.lists.length === 0) {
      console.log('📊 No lists found, data should be loaded from persona');
    } else {
      console.log(`📊 Loaded ${this.lists.length} lists from localStorage`);
    }
  }

  // Get all lists
  getAllLists(): List[] {
    return [...this.lists]
      .sort((a, b) => a.priority - b.priority)
      .map(list => ({
        ...list,
        items: [...list.items].sort((a, b) => a.priority - b.priority)
      }));
  }

  // Get lists by status
  getListsByStatus(status: string): List[] {
    return this.lists.filter(list => list.status === status)
      .sort((a, b) => a.priority - b.priority)
      .map(list => ({
        ...list,
        items: [...list.items].sort((a, b) => a.priority - b.priority)
      }));
  }

  // Get list by ID
  getListById(id: string): List | undefined {
    const list = this.lists.find(list => list.id === id);
    if (list) {
      // Return list with items sorted by priority
      return {
        ...list,
        items: [...list.items].sort((a, b) => a.priority - b.priority)
      };
    }
    return undefined;
  }

  // Get lists by goal ID
  getListsByGoalId(goalId: string): List[] {
    return this.lists.filter(list => list.goalId === goalId)
      .sort((a, b) => a.priority - b.priority)
      .map(list => ({
        ...list,
        items: [...list.items].sort((a, b) => a.priority - b.priority)
      }));
  }

  // Create new list
  async createList(data: ListFormData): Promise<List> {
    // console.log('🆕 Creating new list with goal-scoped priority:', data.priority);
    // console.log('🎯 Target goal:', data.goalId);

    // Validate that lists have goalId
    if (!data.goalId) {
      throw new Error('Goal selection is required for all lists');
    }
    
    const targetPriority = data.priority;
    
    // Check if the target priority is already taken within the same goal
    const existingAtPriority = this.lists.find(l => 
      l.priority === targetPriority && l.goalId === data.goalId
    );
    
    if (existingAtPriority) {
      // console.log(`⚠️ Priority ${targetPriority} already exists in goal ${data.goalId}, adjusting priorities`);
      // Shift priorities within the goal
      this.lists.forEach(list => {
        if (list.goalId === data.goalId && list.priority >= targetPriority) {
          list.priority += 1;
          list.updatedAt = new Date();
        }
      });
    }

    // Create items with proper structure
    const items: ListItem[] = (data.items || []).map((item, index) => ({
      id: this.generateId('item'),
      ...item,
      priority: index + 1,
      createdAt: new Date(),
      updatedAt: new Date(),
    }));

    const newList: List = {
      id: this.generateId('list'),
      ...data,
      priority: targetPriority,
      // REMOVED: progress, totalItems, completedItems
      items,
      createdAt: new Date(),
      updatedAt: new Date(),
    };

    this.lists.push(newList);
    
    // Normalize priorities within this goal
    if (data.goalId) {
      this.normalizeGoalPriorities(data.goalId);
    } else {
      this.normalizeAllPriorities();
    }
    
    // console.log(`✅ Created list "${newList.title}" with priority ${newList.priority} in goal ${data.goalId}`);
    this.saveLists();
    
    return newList;
  }

  // Update list
  async updateList(id: string, updates: Partial<List>): Promise<List> {
    const listIndex = this.lists.findIndex(list => list.id === id);
    if (listIndex === -1) {
      throw new Error('List not found');
    }

    const updatedList = {
      ...this.lists[listIndex],
      ...updates,
      updatedAt: new Date(),
    };

    // Handle priority changes within goal scope
    if (updates.priority && updates.priority !== this.lists[listIndex].priority) {
      this.adjustGoalPriorities(updates.priority, id, updatedList.goalId);
    }

    // REMOVED: Progress and status updates - handled by Universal Completion Service

    this.lists[listIndex] = updatedList;
    this.saveLists();
    
    return updatedList;
  }

  // Delete list
  async deleteList(id: string): Promise<void> {
    const listIndex = this.lists.findIndex(list => list.id === id);
    if (listIndex === -1) {
      throw new Error('List not found');
    }

    // Get the goalId of the list being deleted
    const deletedList = this.lists[listIndex];
    const goalId = deletedList.goalId;
    
    // Delete the list
    this.lists.splice(listIndex, 1);
    
    // Normalize priorities within the goal after deletion
    if (goalId) {
      // console.log(`🔧 Normalizing priorities for goal ${goalId} after list deletion`);
      this.normalizeGoalPriorities(goalId);
    }
    
    this.saveLists();
  }

  // Clone template to create new list
  async cloneTemplate(templateId: string, customizations?: Partial<ListFormData>): Promise<List> {
    const template = templatesService.getTemplateById(templateId);
    if (!template) {
      throw new Error('Template not found');
    }

    // Update usage count via templates service
    templatesService.incrementUsageCount(templateId);

    const listData: ListFormData = {
      title: template.title,
      description: template.description,
      status: 'ready',
      priority: 1,
      color: template.color,
      items: template.items.map(item => ({
        content: item.content,
        updatedAt: new Date(),
      })),
      ...customizations,
    };

    const newList = await this.createList(listData);
    newList.templateId = templateId;

    return newList;
  }

  // Reorder lists (within goal scope)
  async reorderLists(reorderedLists: List[]): Promise<void> {
    // console.log('🔧 Lists Service: Starting reorderLists within goal scope');
    // console.log('📝 Lists Service: Received reordered lists:', 
    //   reorderedLists.map(l => ({ id: l.id, title: l.title, priority: l.priority, goalId: l.goalId }))
    // );
    
    if (reorderedLists.length === 0) {
      // console.log('🤷 Lists Service: No lists to reorder');
      return;
    }
    
    // Get the goal ID (all lists should have the same goal when reordering)
    const goalId = reorderedLists[0].goalId;
    
    // Update priorities for all lists in the reordered list
    reorderedLists.forEach((reorderedList, index) => {
      const existingList = this.lists.find(l => l.id === reorderedList.id);
      if (existingList) {
        const newPriority = index + 1; // Sequential priorities within goal
        // console.log(`🎯 Lists Service: Updating "${existingList.title}" priority: ${existingList.priority} → ${newPriority}`);
        existingList.priority = newPriority;
        existingList.updatedAt = new Date();
      }
    });
    
    // Normalize priorities within the goal to ensure no gaps
    if (goalId) {
      this.normalizeGoalPriorities(goalId);
    } else {
      // If no goalId, normalize all priorities (fallback)
      this.normalizeAllPriorities();
    }
    
    // console.log('✨ Lists Service: Final lists after goal-scoped reorder:', 
    //   this.lists
    //     .filter(l => l.goalId === goalId)
    //     .sort((a, b) => a.priority - b.priority)
    //     .map(l => ({ id: l.id, title: l.title, priority: l.priority }))
    // );
    
    this.saveLists();
  }

  // Add item to list
  async addItem(listId: string, content: string, itemData?: Partial<ListItem>): Promise<ListItem> {
    const list = this.lists.find(l => l.id === listId);
    if (!list) {
      throw new Error('List not found');
    }

    const now = new Date();
    const newItem: ListItem = {
      id: this.generateId('item'),
      content,
      priority: list.items.length + 1,
      createdAt: now,
      updatedAt: now,
      // Enhanced features with defaults
      history: [{
        id: this.generateId('history'),
        action: 'created',
        timestamp: now,
        note: 'Item created'
      }],
      ...itemData, // Allow overriding defaults
    };

    list.items.push(newItem);
    list.updatedAt = new Date();
    
    // REMOVED: Progress and status updates - handled by Universal Completion Service
    
    this.saveLists();
    return newItem;
  }

  // Update item
  async updateItem(listId: string, itemId: string, updates: Partial<ListItem>): Promise<ListItem> {
    const list = this.lists.find(l => l.id === listId);
    if (!list) {
      throw new Error('List not found');
    }

    const itemIndex = list.items.findIndex(i => i.id === itemId);
    if (itemIndex === -1) {
      throw new Error('Item not found');
    }

    const originalItem = list.items[itemIndex];
    const now = new Date();
    
    // Remove completion-related updates - these are handled by UniversalCompletionService
    const allowedUpdates = { ...updates };
    delete (allowedUpdates as any).completed;
    delete (allowedUpdates as any).completedAt;

    // Track changes for history
    const changes: Record<string, { from: any; to: any }> = {};
    Object.keys(allowedUpdates).forEach(key => {
      if (allowedUpdates[key as keyof ListItem] !== originalItem[key as keyof ListItem]) {
        changes[key] = {
          from: originalItem[key as keyof ListItem],
          to: allowedUpdates[key as keyof ListItem]
        };
      }
    });

    const updatedItem = {
      ...originalItem,
      ...allowedUpdates,
      updatedAt: now,
    };

    // Add history entry if there are changes
    if (Object.keys(changes).length > 0) {
      const historyEntry = {
        id: this.generateId('history'),
        action: 'updated' as 'updated',
        timestamp: now,
        changes,
        note: this.generateChangeDescription(changes)
      };

      updatedItem.history = [...(originalItem.history || []), historyEntry];
    }

    list.items[itemIndex] = updatedItem;
    list.updatedAt = now;
    
    // REMOVED: Progress and status updates - handled by Universal Completion Service
    
    this.saveLists();
    return updatedItem;
  }

  // Delete item
  async deleteItem(listId: string, itemId: string): Promise<void> {
    const list = this.lists.find(l => l.id === listId);
    if (!list) {
      throw new Error('List not found');
    }

    const itemIndex = list.items.findIndex(i => i.id === itemId);
    if (itemIndex === -1) {
      throw new Error('Item not found');
    }

    list.items.splice(itemIndex, 1);
    list.updatedAt = new Date();
    
    // Normalize item priorities after deletion
    // console.log(`🔧 Normalizing item priorities in list ${listId} after item deletion`);
    list.items.sort((a, b) => a.priority - b.priority);
    list.items.forEach((item, index) => {
      item.priority = index + 1;
      item.updatedAt = new Date();
    });
    
    // REMOVED: Progress and status updates - handled by Universal Completion Service
    
    this.saveLists();
  }

  // New method for contextual item updates
  async updateItemCompletion(listId: string, itemId: string, completed: boolean, contextDate: Date = new Date()): Promise<void> {
    universalCompletionService.updateItemCompletion(listId, itemId, completed, contextDate);
  }

  // New method to get contextual list data
  getListDataForContext(listId: string, contextDate: Date = new Date()): DateScopedListData | null {
    const list = this.getListById(listId);
    if (!list) return null;
    
    return universalCompletionService.getListDataForContext(list, contextDate);
  }

  // Helper to get current context data (for backward compatibility)
  getCurrentListData(listId: string): DateScopedListData | null {
    return this.getListDataForContext(listId, new Date());
  }

  // Reorder items within a list (drag-and-drop)
  async reorderItems(listId: string, itemId: string, newPriority: number): Promise<void> {
    const list = this.lists.find(l => l.id === listId);
    if (!list) {
      throw new Error('List not found');
    }

    const itemIndex = list.items.findIndex(i => i.id === itemId);
    if (itemIndex === -1) {
      throw new Error('Item not found');
    }

    const item = list.items[itemIndex];
    const oldPriority = item.priority;

    // Update item priority and add history entry
    item.priority = newPriority;
    item.updatedAt = new Date();
    item.history = [...(item.history || []), {
      id: this.generateId('history'),
      action: 'moved',
      timestamp: new Date(),
      changes: { priority: { from: oldPriority, to: newPriority } },
      note: `Moved from position ${oldPriority} to ${newPriority}`
    }];

    // Adjust other items' priorities
    list.items.forEach(otherItem => {
      if (otherItem.id !== itemId) {
        if (oldPriority < newPriority && otherItem.priority > oldPriority && otherItem.priority <= newPriority) {
          otherItem.priority -= 1;
          otherItem.updatedAt = new Date();
        } else if (oldPriority > newPriority && otherItem.priority >= newPriority && otherItem.priority < oldPriority) {
          otherItem.priority += 1;
          otherItem.updatedAt = new Date();
        }
      }
    });

    list.updatedAt = new Date();
    this.saveLists();
  }

  async reorderItemsArray(listId: string, reorderedItems: ListItem[]): Promise<void> {
    const list = this.lists.find(l => l.id === listId);
    if (!list) {
      throw new Error('List not found');
    }

    // console.log('🔧 Service: Starting reorderItemsArray for list:', listId);
    // console.log('📝 Service: Received reordered items:', 
    //   reorderedItems.map(i => ({ id: i.id, content: i.content, priority: i.priority }))
    // );
    // console.log('📚 Service: Current items before reorder:', 
    //   list.items.map(i => ({ id: i.id, content: i.content, priority: i.priority }))
    // );

    if (reorderedItems.length === 0) {
      // console.log('🤷 Service: No items to reorder');
      return;
    }
    
    // Use Goals service approach: directly apply sequential priorities from reordered array
    // This matches the working behavior in GoalsList component
    reorderedItems.forEach((reorderedItem, index) => {
      const existingItem = list.items.find(i => i.id === reorderedItem.id);
      if (existingItem) {
        const newPriority = index + 1; // Sequential priorities starting from 1
        const oldPriority = existingItem.priority;
        
        if (oldPriority !== newPriority) {
          // console.log(`🎯 Service: Updating "${existingItem.content}" priority: ${oldPriority} → ${newPriority}`);
          existingItem.priority = newPriority;
          existingItem.updatedAt = new Date();
        }
      }
    });
    
    // console.log('✨ Service: Final items with sequential priorities:', 
    //   list.items.map(i => ({ id: i.id, content: i.content, priority: i.priority }))
    // );

    list.updatedAt = new Date();
    this.saveLists();
    
    // Invalidate Universal Completion System cache since item order changed
    universalCompletionService.invalidateListCache(listId);
    // console.log('🗑️ Service: Invalidated completion cache for list after reordering');
  }

  // Bulk operations on items
  async performItemBulkOperation(listId: string, operation: any): Promise<void> {
    const list = this.lists.find(l => l.id === listId);
    if (!list) {
      throw new Error('List not found');
    }

    const { type, itemIds, payload } = operation;
    const itemsToUpdate = list.items.filter(item => itemIds.includes(item.id));
    const now = new Date();

    itemsToUpdate.forEach(item => {
      const changes: Record<string, { from: any; to: any }> = {};

      switch (type) {
        case 'complete':
        case 'uncomplete':
          // Completion logic now handled by Universal Completion Service
          // This method should be updated to use the new service
          console.warn('Bulk completion operations should use Universal Completion Service');
          break;
        case 'priority_update':
          if (item.priority !== payload.priority) {
            changes.priority = { from: item.priority, to: payload.priority };
            item.priority = payload.priority;
          }
          break;
        case 'delete':
          // Will be handled separately
          break;
      }

      if (Object.keys(changes).length > 0) {
        item.updatedAt = now;
        item.history = [...(item.history || []), {
          id: this.generateId('history'),
          action: type === 'complete' ? 'completed' : type === 'uncomplete' ? 'uncompleted' : 'updated',
          timestamp: now,
          changes,
          note: this.generateChangeDescription(changes)
        }];
      }
    });

    if (type === 'delete') {
      list.items = list.items.filter(item => !itemIds.includes(item.id));
    }

    list.updatedAt = now;
    // REMOVED: Progress and status updates - handled by Universal Completion Service
    this.saveLists();
  }

  // Get the next incomplete item in a list
  getNextIncompleteItem(listId: string, contextDate: Date = new Date()): ListItem | null {
    const listData = this.getListDataForContext(listId, contextDate);
    if (!listData) {
      return null;
    }

    // Sort items by priority and find the first incomplete item
    const sortedItems = [...listData.items].sort((a, b) => a.priority - b.priority);
    const nextIncomplete = sortedItems.find(item => !item.completed);
    
    return nextIncomplete || null;
  }

  // Complete the next incomplete item and return the following incomplete item
  async completeNextItemAndGetNext(listId: string, contextDate: Date = new Date()): Promise<{ completedItem: ListItem | null; nextItem: ListItem | null }> {
    const nextItem = this.getNextIncompleteItem(listId, contextDate);
    
    if (!nextItem) {
      return { completedItem: null, nextItem: null };
    }

    // Complete the item using universal completion service
    await this.updateItemCompletion(listId, nextItem.id, true, contextDate);
    
    // Get the next incomplete item after completion
    const followingItem = this.getNextIncompleteItem(listId, contextDate);
    
    return { 
      completedItem: nextItem, 
      nextItem: followingItem 
    };
  }

  // Search items within lists
  searchItems(listId: string, query: string): ListItem[] {
    const list = this.lists.find(l => l.id === listId);
    if (!list) {
      return [];
    }

    const searchLower = query.toLowerCase();
    return list.items.filter(item =>
      item.content.toLowerCase().includes(searchLower) ||
      item.description?.toLowerCase().includes(searchLower) ||
      item.notes?.toLowerCase().includes(searchLower)
    );
  }

  // Filter and sort items
  getFilteredItems(listId: string, filters: any, sort?: any, contextDate: Date = new Date()): ListItem[] {
    const listData = this.getListDataForContext(listId, contextDate);
    if (!listData) {
      return [];
    }

    let filtered = [...listData.items];

    // Apply filters
    if (filters.completed !== undefined) {
      filtered = filtered.filter(item => item.completed === filters.completed);
    }


    if (filters.category) {
      filtered = filtered.filter(item => item.category === filters.category);
    }

    if (filters.difficulty && filters.difficulty.length > 0) {
      filtered = filtered.filter(item =>
        item.difficulty && filters.difficulty.includes(item.difficulty)
      );
    }


    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(item =>
        item.content.toLowerCase().includes(searchLower) ||
        item.description?.toLowerCase().includes(searchLower) ||
        item.notes?.toLowerCase().includes(searchLower)
      );
    }

    if (filters.hasDeadline !== undefined) {
      filtered = filtered.filter(item =>
        filters.hasDeadline ? item.dueDate : !item.dueDate
      );
    }

    if (filters.isOverdue) {
      const now = new Date();
      filtered = filtered.filter(item =>
        item.dueDate && item.dueDate < now && !item.completed
      );
    }

    if (filters.hasDependencies) {
      filtered = filtered.filter(item =>
        item.dependsOn && item.dependsOn.length > 0
      );
    }

    if (filters.isBlocked) {
      filtered = filtered.filter(item =>
        item.blockedBy && item.blockedBy.length > 0
      );
    }

    // Apply sorting
    if (sort) {
      filtered.sort((a, b) => {
        let aValue: any;
        let bValue: any;

        switch (sort.field) {
          case 'content':
            aValue = a.content.toLowerCase();
            bValue = b.content.toLowerCase();
            break;
          case 'priority':
            aValue = a.priority;
            bValue = b.priority;
            break;
          case 'createdAt':
            aValue = new Date(a.createdAt).getTime();
            bValue = new Date(b.createdAt).getTime();
            break;
          case 'updatedAt':
            aValue = new Date(a.updatedAt).getTime();
            bValue = new Date(b.updatedAt).getTime();
            break;
          case 'dueDate':
            aValue = a.dueDate ? a.dueDate.getTime() : 0;
            bValue = b.dueDate ? b.dueDate.getTime() : 0;
            break;
          default:
            aValue = a.priority;
            bValue = b.priority;
        }

        if (aValue < bValue) return sort.direction === 'asc' ? -1 : 1;
        if (aValue > bValue) return sort.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return filtered;
  }

  // Get list statistics
  getListStats(contextDate: Date = new Date()): ListStats {
    const now = new Date();
    
    const stats: ListStats = {
      total: this.lists.length,
      byStatus: {
        ready: 0,
        in_progress: 0,
        complete: 0,
      },
      byProgress: {
        notStarted: 0,
        inProgress: 0,
        completed: 0,
      },
      averageCompletionTime: 0,
      totalItems: 0,
      completedItems: 0,
      overdue: 0,
    };

    let totalCompletionDays = 0;
    let completedCount = 0;

    this.lists.forEach(list => {
      const listData = this.getListDataForContext(list.id, contextDate);
      
      // Status counts
      stats.byStatus[list.status] += 1;
      
      // Progress counts (using contextual data)
      if (listData) {
        if (listData.progress === 0) stats.byProgress.notStarted += 1;
        else if (listData.progress === 100) stats.byProgress.completed += 1;
        else stats.byProgress.inProgress += 1;
        
        // Item counts
        stats.totalItems += listData.totalItems;
        stats.completedItems += listData.completedItems;
      }
      
      // Overdue count
      if (list.deadline && list.deadline < now && list.status !== 'complete') {
        stats.overdue += 1;
      }
      
      // Completion time calculation
      if (list.completedDate && list.startDate) {
        const days = Math.ceil((list.completedDate.getTime() - list.startDate.getTime()) / (1000 * 60 * 60 * 24));
        totalCompletionDays += days;
        completedCount += 1;
      }
    });

    stats.averageCompletionTime = completedCount > 0 ? totalCompletionDays / completedCount : 0;

    return stats;
  }

  // Bulk operations
  async performBulkOperation(operation: BulkOperation): Promise<void> {
    const { type, listIds, payload } = operation;
    
    const listsToUpdate = this.lists.filter(list => listIds.includes(list.id));
    
    listsToUpdate.forEach(list => {
      switch (type) {
        case 'status_change':
          list.status = payload.status;
          // REMOVED: this.updateListStatus(list) - handled by Universal Completion Service
          break;
        case 'goal_link':
          list.goalId = payload.goalId;
          break;
        case 'archive':
          list.status = 'complete';
          list.completedDate = new Date();
          break;
        case 'delete':
          // Will be handled separately
          break;
      }
      list.updatedAt = new Date();
    });

    if (type === 'delete') {
      this.lists = this.lists.filter(list => !listIds.includes(list.id));
    }

    this.saveLists();
  }

  // Private helper methods
  private generateId(prefix: string = 'list'): string {
    return `${prefix}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private normalizeAllPriorities(): void {
    // console.log('🔧 Lists Service: Normalizing all priorities to ensure uniqueness');
    
    // Group lists by goal
    const listsByGoal = this.lists.reduce((acc, list) => {
      const goalKey = list.goalId || 'no-goal';
      if (!acc[goalKey]) {
        acc[goalKey] = [];
      }
      acc[goalKey].push(list);
      return acc;
    }, {} as Record<string, List[]>);

    // Normalize priorities within each goal group
    Object.entries(listsByGoal).forEach(([goalId, lists]) => {
      if (goalId === 'no-goal') {
        // Handle lists without goals separately
        this.normalizeListsWithoutGoal(lists);
      } else {
        this.normalizeGoalPriorities(goalId, lists);
      }
    });

    // console.log('✅ Lists Service: All priorities normalized by goal:', 
    //   this.lists.map(l => ({ id: l.id, title: l.title, priority: l.priority, goalId: l.goalId }))
    // );
  }

  // Helper method to normalize priorities within a specific goal
  private normalizeGoalPriorities(goalId: string, providedLists?: List[]): void {
    // console.log(`🔧 Lists Service: Normalizing priorities for goal ${goalId}`);
    
    // Use provided lists or filter from all lists
    const goalLists = providedLists || this.lists.filter(l => l.goalId === goalId);
    
    // Sort by current priority, then by creation date as tiebreaker
    const sorted = [...goalLists].sort((a, b) => {
      if (a.priority === b.priority) {
        return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
      }
      return a.priority - b.priority;
    });

    // Reassign sequential priorities within the goal
    sorted.forEach((list, index) => {
      const newPriority = index + 1;
      const existingList = this.lists.find(l => l.id === list.id);
      if (existingList) {
        existingList.priority = newPriority;
        existingList.updatedAt = new Date();
      }
    });

    // console.log(`✅ Lists Service: Priorities normalized for goal ${goalId}:`, 
    //   sorted.map(l => ({ id: l.id, title: l.title, priority: l.priority }))
    // );
  }

  // Helper method to normalize priorities for lists without goals
  private normalizeListsWithoutGoal(lists: List[]): void {
    // console.log('🔧 Lists Service: Normalizing priorities for lists without goals');
    
    // Sort by current priority, then by creation date as tiebreaker
    const sorted = [...lists].sort((a, b) => {
      if (a.priority === b.priority) {
        return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
      }
      return a.priority - b.priority;
    });

    // Reassign sequential priorities for lists without goals
    sorted.forEach((list, index) => {
      const newPriority = index + 1;
      const existingList = this.lists.find(l => l.id === list.id);
      if (existingList) {
        existingList.priority = newPriority;
        existingList.updatedAt = new Date();
      }
    });

    // console.log('✅ Lists Service: Priorities normalized for lists without goals:', 
    //   sorted.map(l => ({ id: l.id, title: l.title, priority: l.priority }))
    // );
  }


  private adjustGoalPriorities(newPriority: number, excludeId: string, goalId?: string): void {
    // console.log(`🔄 Lists Service: Adjusting priorities in goal ${goalId} for priority ${newPriority}`);
    
    this.lists.forEach(list => {
      if (list.id !== excludeId && 
          list.goalId === goalId && 
          list.priority >= newPriority) {
        // const oldPriority = list.priority;
        list.priority += 1;
        list.updatedAt = new Date();
        // console.log(`  📈 Adjusted "${list.title}": ${oldPriority} → ${list.priority}`);
      }
    });
  }

  // Helper method to generate change descriptions
  private generateChangeDescription(changes: Record<string, { from: any; to: any }>): string {
    const descriptions: string[] = [];
    
    for (const [field, change] of Object.entries(changes)) {
      switch (field) {
        case 'completed':
          descriptions.push(change.to ? 'Marked as completed' : 'Marked as incomplete');
          break;
        case 'content':
          descriptions.push(`Content updated`);
          break;
        case 'priority':
          descriptions.push(`Priority changed from ${change.from} to ${change.to}`);
          break;
        case 'dueDate':
          descriptions.push(change.to ? 'Due date set' : 'Due date removed');
          break;
        case 'difficulty':
          descriptions.push(`Difficulty changed to ${change.to}`);
          break;
        default:
          descriptions.push(`${field} updated`);
      }
    }
    
    return descriptions.join(', ');
  }
}

// Export singleton instance
export const listsService = new ListsService();